/**
 * usePWA — consolidates all PWA capabilities into a single hook.
 *
 * Returns:
 *   install:   { canInstall, prompt, dismiss, dismissed }
 *   push:      { supported, enabled, permission, subscribe, unsubscribe }
 *   offline:   { isOffline }
 *   sw:        { updateAvailable, applyUpdate }
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api.js';

// ─────────────────────────────────────────────────────────────────
export function usePWA() {
  // ── Install prompt ─────────────────────────────────────────────
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installDismissed, setInstallDismissed] = useState(() =>
    localStorage.getItem('pwa-install-dismissed') === '1'
  );
  const [installed, setInstalled] = useState(() =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );

  useEffect(() => {
    const handlePrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const handleInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', handlePrompt);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handlePrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setInstalled(true);
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const dismissInstall = useCallback(() => {
    setInstallDismissed(true);
    localStorage.setItem('pwa-install-dismissed', '1');
    setDeferredPrompt(null);
  }, []);

  // ── Push notifications ─────────────────────────────────────────
  const pushSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  const [pushEnabled,     setPushEnabled]     = useState(false);
  const [pushPermission,  setPushPermission]  = useState(
    pushSupported ? Notification.permission : 'unsupported'
  );
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    if (!pushSupported) return;
    setPushPermission(Notification.permission);
    navigator.serviceWorker.ready.then(reg =>
      reg.pushManager.getSubscription().then(sub => setPushEnabled(!!sub))
    );
  }, []);

  const subscribePush = useCallback(async () => {
    if (!pushSupported) return { success: false, reason: 'not_supported' };
    setPushLoading(true);
    try {
      const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidKey) return { success: false, reason: 'no_vapid_key' };

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      await api.post('/push/subscribe', { subscription: sub.toJSON() });
      setPushEnabled(true);
      setPushPermission('granted');
      localStorage.setItem('push-prompt-shown', '1');
      return { success: true };
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setPushPermission('denied');
        return { success: false, reason: 'denied' };
      }
      return { success: false, reason: err.message };
    } finally {
      setPushLoading(false);
    }
  }, []);

  const unsubscribePush = useCallback(async () => {
    if (!pushSupported) return;
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        // Tell the browser to drop the subscription first
        await sub.unsubscribe();
        // Then clear it on the server so we stop sending pushes to a dead endpoint.
        // Best-effort — the 410 cleanup in notifications.js is the safety net.
        api.post('/push/subscribe', { subscription: null }).catch(() => {});
      }
      setPushEnabled(false);
    } finally {
      setPushLoading(false);
    }
  }, []);

  // ── Offline detection ──────────────────────────────────────────
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const goOnline  = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online',  goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // ── SW update detection ────────────────────────────────────────
  const [swUpdate, setSwUpdate] = useState(null); // waiting SW registration

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready.then(reg => {
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setSwUpdate(reg);
          }
        });
      });
    });

    // Listen for SW messages
    navigator.serviceWorker.addEventListener('message', (e) => {
      if (e.data?.type === 'SYNC_COMPLETE') {
        window.dispatchEvent(new CustomEvent('gameday:sync', { detail: e.data }));
      }
    });
  }, []);

  const applySwUpdate = useCallback(() => {
    if (!swUpdate) return;
    swUpdate.waiting?.postMessage({ type: 'SKIP_WAITING' });
    window.location.reload();
  }, [swUpdate]);

  return {
    install: {
      canInstall: !!deferredPrompt && !installed && !installDismissed,
      isInstalled: installed,
      prompt: promptInstall,
      dismiss: dismissInstall,
    },
    push: {
      supported: pushSupported,
      enabled: pushEnabled,
      permission: pushPermission,
      loading: pushLoading,
      subscribe: subscribePush,
      unsubscribe: unsubscribePush,
      neverAsked: !localStorage.getItem('push-prompt-shown') && pushPermission === 'default',
    },
    offline: { isOffline },
    sw: { updateAvailable: !!swUpdate, apply: applySwUpdate },
  };
}

// ── Helper ─────────────────────────────────────────────────────
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}
